import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../lib/supabase";
import TEAMS from "../sport/teams.json";
import {
  FORMATIONS,
  FORMATION_NAMES,
  DEFAULT_FORMATION,
  findCompatibleSlots,
  findDragCompatibleSlots,
} from "../sport/formation";
import ActionButton from "../components/ActionButton";
import RerollButton from "../components/RerollButton";
import LockInButton from "../components/LockInButton";
import FormationSelector from "../components/FormationSelector";
import TeamDisplay from "../sport/components/TeamDisplay";
import SpinningDisplay from "../components/SpinningDisplay";
import SoccerField from "../sport/components/Field";
import PlayerCard from "../sport/components/PlayerCard";
import ResultsModal from "../components/ResultsModal";
import ConfirmDialog from "../components/ConfirmDialog";
import Header from "../components/Header";
import { useAuth } from "../contexts/AuthContext";
import { projectSeason, teamStrength, ratingLetter } from "../sport/projection";
import MobilePitchBar from "../sport/components/MobileLineupBar";
import { applyFormMods } from "../sport/formMods";
import TradeWindowModal from "../sport/components/TradeWindowModal";

const SPIN_TICKS = 16;
const SPIN_INTERVAL_MS = 100;

export default function GamePage() {
  const [team, setTeam] = useState(null);
  const [spinning, setSpinning] = useState(false);
  const [spinMode, setSpinMode] = useState(null);
  const [spinDisplay, setSpinDisplay] = useState(null);
  const [selectedPlayer, setSelectedPlayer] = useState(null);
  const [manualSlot, setManualSlot] = useState(null);
  const [roster, setRoster] = useState({});
  const [lockedThisRound, setLockedThisRound] = useState(false);
  const [lastLock, setLastLock] = useState(null);
  const [showResults, setShowResults] = useState(false);
  const [isPersonalBest, setIsPersonalBest] = useState(false);
  const [viewedPlayer, setViewedPlayer] = useState(null);
  const [confirmingReset, setConfirmingReset] = useState(false);
  const [formation, setFormation] = useState(DEFAULT_FORMATION);
  const [dragging, setDragging] = useState(null);
  const [tradeOpen, setTradeOpen] = useState(false);
  const tradeDoneRef = useRef(false);
  const { user } = useAuth();

  const navigate = useNavigate();
  const [confirmingLeave, setConfirmingLeave] = useState(false);

  const slots = FORMATIONS[formation];
  const displayPlayer = viewedPlayer || selectedPlayer;

  const REROLL_LIMIT = 2;
  const [rerollsLeft, setRerollsLeft] = useState({
    team: REROLL_LIMIT,
    year: REROLL_LIMIT,
  });

  const intervalRef = useRef(null);
  useEffect(
    () => () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    },
    [],
  );

  const compatibleSlots = findCompatibleSlots(selectedPlayer, roster, slots);
  const previewSlot =
    manualSlot && compatibleSlots.includes(manualSlot)
      ? manualSlot
      : compatibleSlots[0] || null;

  const filledSlots = Object.keys(roster).length;
  const totalSlots = slots.length;
  const isComplete = filledSlots === totalSlots;
  const formationLocked = filledSlots > 0;
  const hasProgress = filledSlots > 0 && !isComplete;
  const runSavedRef = useRef(false);

  useEffect(() => {
    const tradeAt = Math.ceil(slots.length / 2); // 6 for an 11-slot XI
    if (!tradeDoneRef.current && !isComplete && filledSlots === tradeAt) {
      tradeDoneRef.current = true;
      setTradeOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filledSlots, isComplete]);

  useEffect(() => {
    if (!isComplete || !user || runSavedRef.current) return;
    runSavedRef.current = true;

    const season = projectSeason(roster, slots);
    const strength = teamStrength(roster, slots);
    const rating = ratingLetter(season);
    const points = season.wins * 3 + season.draws;

    (async () => {
      // Previous best (before this run is saved) → personal-best detection
      const { data: prev } = await supabase
        .from("runs")
        .select("wins, draws")
        .eq("user_id", user.id)
        .eq("mode", "standard");
      const prevBest = (prev || []).reduce(
        (m, r) => Math.max(m, r.wins * 3 + r.draws),
        -1,
      );
      if (prevBest >= 0 && points > prevBest) setIsPersonalBest(true);

      const { error: runErr } = await supabase.from("runs").insert({
        user_id: user.id,
        mode: "standard",
        formation,
        wins: season.wins,
        draws: season.draws,
        losses: season.losses,
        rating,
        strength,
        roster,
      });
      if (runErr) console.warn("Failed to save run:", runErr);

      const { error: lbErr } = await supabase.rpc("submit_standard_run", {
        p_mode: "standard",
        p_wins: season.wins,
        p_draws: season.draws,
        p_losses: season.losses,
        p_rating: rating,
        p_strength: strength,
        p_formation: formation,
        p_roster: roster,
      });
      if (lbErr) console.warn("Failed to submit to leaderboard:", lbErr);
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isComplete, user]);

  const dragCompatibleSlots = dragging
    ? findDragCompatibleSlots(
        dragging.player,
        roster,
        slots,
        dragging.sourceSlotId,
      )
    : [];

  useEffect(() => {
    if (isComplete) setShowResults(true);
  }, [isComplete]);

  useEffect(() => {
    if (!hasProgress) return;
    const onBeforeUnload = (e) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [hasProgress]);

  const canLockIn =
    !isComplete &&
    !!team &&
    !!selectedPlayer &&
    !!previewSlot &&
    !lockedThisRound &&
    !spinning;
  const canSpin = !isComplete && !spinning && (!team || lockedThisRound);
  //const spinGlows = !!team && lockedThisRound && !spinning;

  const sameSeasonAlternatives = team
    ? TEAMS.filter((t) => t.season === team.season && t.id !== team.id)
    : [];
  const sameClubAlternatives = team
    ? TEAMS.filter((t) => t.club === team.club && t.id !== team.id)
    : [];

  const canRerollTeam =
    !!team &&
    !spinning &&
    !lockedThisRound &&
    sameSeasonAlternatives.length > 0 &&
    rerollsLeft.team > 0;
  const canRerollYear =
    !!team &&
    !spinning &&
    !lockedThisRound &&
    sameClubAlternatives.length > 0 &&
    rerollsLeft.year > 0;

  const handleConfirmLeave = () => {
    setConfirmingLeave(false);
    navigate("/");
  };

  const handlePlayerClick = (p) => {
    if (lockedThisRound || spinning) return;
    setSelectedPlayer(p);
    setManualSlot(null);
    setViewedPlayer(null);
  };

  const handleSlotClick = (slotId) => {
    if (lockedThisRound || spinning) return;
    if (compatibleSlots.includes(slotId)) setManualSlot(slotId);
  };

  const handleLockedSlotClick = (slotId) => {
    const p = roster[slotId];
    if (!p) return;
    setViewedPlayer(p);
    setSelectedPlayer(null);
    setManualSlot(null);
  };

  const handleDragStart = (sourceSlotId, isLocked) => {
    if (isLocked) {
      const player = roster[sourceSlotId];
      if (!player) return;
      setDragging({ player, sourceSlotId, isLocked: true });
      // Clear any selection focus so the drag is unambiguous
      setSelectedPlayer(null);
      setManualSlot(null);
      setViewedPlayer(null);
    } else {
      if (!selectedPlayer) return;
      setDragging({ player: selectedPlayer, sourceSlotId, isLocked: false });
    }
  };

  const handleDragEnd = () => setDragging(null);

  const handleDrop = (targetSlotId) => {
    if (!dragging) return;
    if (!dragCompatibleSlots.includes(targetSlotId)) {
      setDragging(null);
      return;
    }

    if (dragging.isLocked) {
      const targetPlayer = roster[targetSlotId];
      setRoster((r) => {
        const next = { ...r };
        if (targetPlayer) {
          // Swap
          next[dragging.sourceSlotId] = targetPlayer;
          next[targetSlotId] = dragging.player;
        } else {
          // Move
          delete next[dragging.sourceSlotId];
          next[targetSlotId] = dragging.player;
        }
        return next;
      });
      // Keep Undo accurate: if the just-locked-this-round player moved, update lastLock's slot.
      if (lastLock && dragging.sourceSlotId === lastLock.slot) {
        setLastLock({ ...lastLock, slot: targetSlotId });
      }
    } else {
      // Preview drag = same as clicking an eligible alternative slot
      setManualSlot(targetSlotId);
    }

    setDragging(null);
  };

  const placePlayer = (slotId) => {
    if (!team || !selectedPlayer || lockedThisRound || spinning) return;
    if (!compatibleSlots.includes(slotId)) return;
    const lockedPlayer = {
      ...selectedPlayer,
      teamId: team.id,
      club: team.club,
      season: team.season,
      fifaEdition: team.fifaEdition,
    };
    setRoster((r) => ({ ...r, [slotId]: lockedPlayer }));
    setLastLock({ slot: slotId, player: selectedPlayer });
    setSelectedPlayer(null);
    setManualSlot(null);
    setLockedThisRound(true);
  };

  const handleLockIn = () => {
    if (!canLockIn || !previewSlot) return;
    placePlayer(previewSlot);
  };

  const handleUndo = () => {
    if (!lastLock) return;
    setRoster((r) => {
      const next = { ...r };
      delete next[lastLock.slot];
      return next;
    });
    setSelectedPlayer(lastLock.player);
    setManualSlot(lastLock.slot);
    setLockedThisRound(false);
    setLastLock(null);
  };

  const runSpin = (mode) => {
    let pool;
    if (mode === "team" && team) pool = sameSeasonAlternatives;
    else if (mode === "year" && team) pool = sameClubAlternatives;
    else pool = TEAMS;
    if (pool.length === 0) return;

    if (mode === "team") setRerollsLeft((r) => ({ ...r, team: r.team - 1 }));
    else if (mode === "year")
      setRerollsLeft((r) => ({ ...r, year: r.year - 1 }));

    if (intervalRef.current) clearInterval(intervalRef.current);

    setSpinning(true);
    setSpinMode(mode);
    setSelectedPlayer(null);
    setManualSlot(null);
    setLastLock(null);
    setViewedPlayer(null);
    if (mode === "full") setLockedThisRound(false);

    setSpinDisplay(pool[Math.floor(Math.random() * pool.length)]);

    let ticks = 0;
    intervalRef.current = setInterval(() => {
      setSpinDisplay(pool[Math.floor(Math.random() * pool.length)]);
      ticks++;
      if (ticks >= SPIN_TICKS) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
        const finalTeam = pool[Math.floor(Math.random() * pool.length)];
        setTeam(applyFormMods(finalTeam));
        //setTeam(finalTeam);
        setSpinDisplay(null);
        setSpinMode(null);
        setSpinning(false);
      }
    }, SPIN_INTERVAL_MS);
  };

  const handleSpin = () => {
    if (canSpin) runSpin("full");
  };
  const handleRerollTeam = () => {
    if (canRerollTeam) runSpin("team");
  };
  const handleRerollYear = () => {
    if (canRerollYear) runSpin("year");
  };

  const handleReset = () => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setRoster({});
    setSelectedPlayer(null);
    setManualSlot(null);
    setTeam(null);
    setSpinDisplay(null);
    setSpinMode(null);
    setSpinning(false);
    setLockedThisRound(false);
    setLastLock(null);
    setShowResults(false);
    setViewedPlayer(null);
    setFormation(DEFAULT_FORMATION);
    runSavedRef.current = false;
    setIsPersonalBest(false);
    tradeDoneRef.current = false;
    setTradeOpen(false);
    setRerollsLeft({ team: REROLL_LIMIT, year: REROLL_LIMIT });
  };
  const handleTrade = (sid, newPlayer) => {
    setRoster((r) => ({ ...r, [sid]: { ...newPlayer } }));
    setLastLock(null); // outgoing player is gone; undo no longer valid
    setViewedPlayer(null);
    setTradeOpen(false);
  };
  const handleSkipTrade = () => setTradeOpen(false);

  let statusHint = null;
  if (!isComplete && !spinning) {
    if (!team && !formationLocked) {
      statusHint = (
        <span className="text-xs text-zinc-500">Choose a formation</span>
      );
    } else if (team) {
      if (lockedThisRound) {
        statusHint = (
          <span className="text-xs text-emerald-400">
            Locked in — spin for next team
          </span>
        );
      } else if (!selectedPlayer) {
        statusHint = (
          <span className="text-xs text-zinc-500">Pick a player</span>
        );
      } else if (compatibleSlots.length === 0) {
        statusHint = (
          <span className="text-xs text-amber-400">
            No slot for {selectedPlayer.pos}
          </span>
        );
      } else if (compatibleSlots.length > 1) {
        statusHint = (
          <span className="text-xs text-zinc-500">
            {compatibleSlots.length} slots · click one to choose
          </span>
        );
      }
    }
  }

  return (
    <div className="h-screen flex flex-col">
      <Header
        onLogoClick={hasProgress ? () => setConfirmingLeave(true) : undefined}
      >
        {filledSlots > 0 && !isComplete && (
          <button
            onClick={() => setConfirmingReset(true)}
            className="text-xs text-zinc-500 hover:text-zinc-300 transition mr-1"
          >
            Reset
          </button>
        )}
        {isComplete && (
          <div className="flex items-center gap-2">
            {!showResults && (
              <button
                onClick={() => setShowResults(true)}
                className="px-4 py-2.5 rounded-full bg-zinc-900 border border-zinc-800 text-sm font-semibold text-zinc-200 hover:border-zinc-700 transition"
              >
                Show results
              </button>
            )}
            {/* <button
              onClick={handleReset}
              className="px-6 py-2.5 rounded-full bg-emerald-500 text-zinc-950 font-semibold text-sm hover:bg-emerald-400 transition"
            >
              Play again
            </button> */}
          </div>
        )}
      </Header>

      <main className="flex-1 min-h-0">
        {/* ===== Desktop (lg+) ===== */}
        <div className="hidden lg:grid h-full p-6 gap-6 grid-cols-[minmax(0,500px)_minmax(0,1fr)_minmax(0,380px)]">
          {spinning ? (
            <SpinningDisplay
              currentTeam={team}
              spinMode={spinMode}
              spinDisplay={spinDisplay}
            />
          ) : team ? (
            <TeamDisplay
              key={team.id}
              team={team}
              selectedId={selectedPlayer?.id}
              roster={roster}
              slots={slots}
              locked={lockedThisRound}
              onPlayerClick={handlePlayerClick}
            />
          ) : (
            <div className="h-full rounded-2xl bg-zinc-900/30 border border-zinc-800 border-dashed grid place-items-center">
              <div className="text-center text-zinc-500">
                <div className="text-zinc-400 mb-1">No team yet</div>
                <div className="text-sm">
                  Press{" "}
                  <span className="text-emerald-400 font-semibold">Spin</span>{" "}
                  to start
                </div>
              </div>
            </div>
          )}

          <div className="flex flex-col h-full min-h-0 gap-3">
            <div className="flex items-center justify-between gap-3 shrink-0">
              <div className="flex items-center gap-2 shrink-0">
                {team && (
                  <>
                    <RerollButton
                      onClick={handleRerollTeam}
                      disabled={!canRerollTeam}
                      label="Team"
                      count={rerollsLeft.team}
                    />
                    <RerollButton
                      onClick={handleRerollYear}
                      disabled={!canRerollYear}
                      label="Year"
                      count={rerollsLeft.year}
                    />
                  </>
                )}
              </div>
              <div className="flex items-center gap-3 shrink-0">
                {!isComplete && (
                  <>
                    {!formationLocked &&
                      !isComplete &&
                      FORMATION_NAMES.length > 1 && (
                        <div className="flex justify-center shrink-0">
                          <FormationSelector
                            formations={FORMATION_NAMES}
                            value={formation}
                            onChange={setFormation}
                            disabled={spinning}
                          />
                        </div>
                      )}
                    <div className="flex-1 flex justify-center min-w-0 px-2">
                      {statusHint}
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.25em] text-zinc-500">
                      Round {filledSlots + 1}/{totalSlots}
                    </span>
                    <ActionButton
                      onClick={handleSpin}
                      disabled={!canSpin}
                      glowing={canSpin}
                      label={
                        spinning && spinMode === "full" ? "Spinning…" : "Spin"
                      }
                    />
                  </>
                )}
              </div>
            </div>

            <div className="flex-1 min-h-0 relative">
              <SoccerField
                slots={slots}
                roster={roster}
                previewSlot={previewSlot}
                previewPlayer={selectedPlayer}
                compatibleSlots={compatibleSlots}
                onSlotClick={handleSlotClick}
                viewedPlayer={viewedPlayer}
                onLockedClick={handleLockedSlotClick}
                dragging={dragging}
                dragCompatibleSlots={dragCompatibleSlots}
                onDragStart={handleDragStart}
                onDragEnd={handleDragEnd}
                onDrop={handleDrop}
              />
              {(canLockIn || lockedThisRound) && (
                <div className="absolute right-4 top-1/2 -translate-y-1/2 z-10">
                  <div className="animate-scale-in">
                    <LockInButton
                      locked={lockedThisRound}
                      onClick={lockedThisRound ? handleUndo : handleLockIn}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>

          <PlayerCard player={displayPlayer} />
        </div>

        {/* ===== Mobile (below lg) ===== */}
        <div className="lg:hidden h-full flex flex-col min-h-0">
          {/* Top controls */}
          <div className="flex items-center gap-2 px-4 py-3 border-b border-zinc-900 shrink-0">
            <div className="flex items-center gap-2">
              {team && !spinning && (
                <>
                  <RerollButton
                    onClick={handleRerollTeam}
                    disabled={!canRerollTeam}
                    label="Team"
                    count={rerollsLeft.team}
                  />
                  <RerollButton
                    onClick={handleRerollYear}
                    disabled={!canRerollYear}
                    label="Year"
                    count={rerollsLeft.year}
                  />
                </>
              )}
            </div>
            <div className="flex-1" />
            {!isComplete && (
              <div className="flex items-center gap-2 shrink-0">
                <span className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                  {filledSlots + 1}/{totalSlots}
                </span>
                {lockedThisRound && (
                  <button
                    onClick={handleUndo}
                    className="text-xs text-zinc-400 hover:text-zinc-200 transition"
                  >
                    Undo
                  </button>
                )}
                <ActionButton
                  onClick={handleSpin}
                  disabled={!canSpin}
                  glowing={canSpin}
                  label={spinning && spinMode === "full" ? "…" : "Spin"}
                />
              </div>
            )}
          </div>

          {/* Formation selector before the first lock-in */}
          {!formationLocked &&
            !isComplete &&
            !spinning &&
            FORMATION_NAMES.length > 1 && (
              <div className="flex justify-center py-2 border-b border-zinc-900 shrink-0">
                <FormationSelector
                  formations={FORMATION_NAMES}
                  value={formation}
                  onChange={setFormation}
                  disabled={spinning}
                />
              </div>
            )}

          {statusHint && (
            <div className="text-center py-1.5 shrink-0">{statusHint}</div>
          )}

          {/* List / spinning / empty */}
          <div className="flex-1 min-h-0 px-3 pt-3">
            {spinning ? (
              <SpinningDisplay
                currentTeam={team}
                spinMode={spinMode}
                spinDisplay={spinDisplay}
              />
            ) : team ? (
              <TeamDisplay
                key={`m-${team.id}`}
                team={team}
                selectedId={selectedPlayer?.id}
                roster={roster}
                slots={slots}
                locked={lockedThisRound}
                onPlayerClick={handlePlayerClick}
              />
            ) : (
              <div className="h-full grid place-items-center text-center text-zinc-500">
                <div>
                  <div className="text-zinc-400 mb-1">No team yet</div>
                  <div className="text-sm">
                    Press{" "}
                    <span className="text-emerald-400 font-semibold">Spin</span>{" "}
                    to start
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Bottom positions pad */}
          {team && !spinning && (
            <MobilePitchBar
              slots={slots}
              roster={roster}
              compatibleSlots={compatibleSlots}
              selectedPlayer={selectedPlayer}
              onPlace={placePlayer}
            />
          )}
        </div>
      </main>

      <ResultsModal
        open={showResults}
        onClose={() => setShowResults(false)}
        roster={roster}
        slots={slots}
        isPersonalBest={isPersonalBest}
        onPlayAgain={handleReset}
      />

      <ConfirmDialog
        open={confirmingReset}
        title="Reset progress?"
        description="You'll lose your current team and all locked-in players for this run."
        confirmLabel="Reset"
        destructive
        onConfirm={() => {
          handleReset();
          setConfirmingReset(false);
        }}
        onCancel={() => setConfirmingReset(false)}
      />
      <ConfirmDialog
        open={confirmingLeave}
        title="Leave this run?"
        description="You'll lose your current team and locked-in players. Progress isn't saved yet."
        confirmLabel="Leave"
        destructive
        onConfirm={handleConfirmLeave}
        onCancel={() => setConfirmingLeave(false)}
      />
      <TradeWindowModal
        open={tradeOpen}
        roster={roster}
        slots={slots}
        onTrade={handleTrade}
        onSkip={handleSkipTrade}
      />
    </div>
  );
}
